import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize, useLocalizedState } from '@treams/treams-hooks';
import { EmojiIcon, ListIcon, TextSizeIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Card,
	Highlight,
	Paragraph,
	PlainInput,
	Popover,
	Select,
	WORKING_AREA_MENU_Z_INDEX,
	WorkingArea,
} from '@treams/treams-ui';

export default function EngagementQuestionCard(props: {
	item: graphql.EngagementQuestion & { category: graphql.EngagementSubcategory };
	types: graphql.EngagementType[];
	new?: boolean;
	query?: string;
	selected?: boolean;
	readOnly?: boolean;
	buttons?: ReactNode;
	onCancel?: () => Promise<void> | void;
	onCreate?: (item: graphql.EngagementQuestion) => void;
	onUpdate?: (item: graphql.EngagementQuestion) => void;
	onSelect?: (item: graphql.EngagementQuestion) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [editing, setEditing] = useState(props.new ?? false);
	const [autoFocus, setAutoFocus] = useState('text');
	const [title, setTitle] = useState(props.item.title);
	const [localizedTitle, setLocalizedTitle] = useLocalizedState(title, setTitle);
	const [typeID, setTypeID] = useState(props.item.type.id);
	const [tooltip, setTooltip] = useState(false);

	const type = props.types.find(({ id }) => id === typeID)!;

	const [create] = graphql.useCreateEngagementQuestionMutation();
	const [update] = graphql.useUpdateEngagementQuestionMutation();

	const onSaveHandler = async () => {
		if (props.new) {
			const { data } = await create({
				variables: { input: { subcategoryID: props.item.category.id, title, typeID } },
			});

			if (props.onCreate && data) {
				props.onCreate(data.createEngagementQuestion.question as graphql.EngagementQuestion);
			}
		} else {
			const { data } = await update({
				variables: { input: { id: props.item.id, title } },
			});

			if (props.onUpdate && data) {
				props.onUpdate(data.updateEngagementQuestion.question as graphql.EngagementQuestion);
			}
		}

		setEditing(false);
	};

	return (
		<WorkingArea
			active={editing}
			onCancel={() => {
				setTitle(props.item.title);
				setTypeID(props.item.type.id);
				props.onCancel?.();
				setEditing(false);
			}}
			onSave={() => onSaveHandler()}
		>
			{(active) => (
				<Card
					selected={active || props.selected}
					onClick={props.onSelect && (() => props.onSelect?.(props.item))}
					flexGrow={1}
					flexShrink={1}
					banner={
						{
							[graphql.EngagementResponseType.Face]: 'yellow',
							[graphql.EngagementResponseType.Open]: 'employee',
							[graphql.EngagementResponseType.PickOne]: 'purple',
						}[type.type]
					}
				>
					<Paragraph>
						{props.readOnly ? (
							localizedTitle
						) : active ? (
							<PlainInput
								multiline
								onChange={setLocalizedTitle}
								onFocus={() => {
									setAutoFocus('text');
									setEditing(true);
								}}
								autoFocus={active && autoFocus === 'text'}
								value={localizedTitle}
							/>
						) : (
							<StyledBox
								width="24vw"
								onMouseDown={() => {
									setAutoFocus('text');
									setEditing(true);
								}}
							>
								<Highlight query={props.query}>{localizedTitle ?? ''}</Highlight>
							</StyledBox>
						)}
					</Paragraph>
					<Flex mt={2} alignItems="center">
						<Popover.Target
							contents={
								<Box p={3}>
									<Paragraph width={300}>
										{t('settings.engagementLibrary.question.type.readonly')}
									</Paragraph>
								</Box>
							}
							visible={tooltip}
							zIndex={WORKING_AREA_MENU_Z_INDEX}
							onDismiss={() => setTooltip(false)}
						>
							<Box
								width="fit-content"
								maxWidth="100%"
								onMouseDown={(event) => {
									if (!props.readOnly) {
										event.stopPropagation();
									}
								}}
								onClick={
									props.readOnly
										? undefined
										: (event) => {
												event.stopPropagation();
												if (props.new) {
													setAutoFocus('type');
													setEditing(true);
												} else {
													setTooltip(true);
												}
											}
								}
							>
								<Select
									small
									fit
									search={false}
									disabled={props.readOnly || !props.new}
									selectedValue={typeID}
									autofocus={active && !!props.new && autoFocus === 'type'}
									zIndex={WORKING_AREA_MENU_Z_INDEX}
									onChange={(id) => {
										if (id) {
											setTypeID(id);
										}
									}}
									options={props.types.map((questionType) => ({
										icon: {
											[graphql.EngagementResponseType.Open]: <TextSizeIcon />,
											[graphql.EngagementResponseType.PickOne]: <ListIcon />,
											[graphql.EngagementResponseType.Face]: <EmojiIcon />,
										}[questionType.type],
										title: localize(questionType.title).text,
										value: questionType.id,
									}))}
								/>
							</Box>
						</Popover.Target>
						{props.buttons}
					</Flex>
				</Card>
			)}
		</WorkingArea>
	);
}

const StyledBox = styled(Box)`
	overflow-wrap: break-word;
`;
